import {
  Card,
  DataTable,
  EmptyState,
  Field,
  PageHeader,
  SelectField,
  StatusPill,
  buttonClasses,
  type Column,
  type StatusTone,
} from '@homecare/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { useHasPermission } from '../../lib/session';

/**
 * The caregiver roster (slice-02 §7).
 *
 * ## The pay column is absent, not blank
 *
 * `basePayRateCents` is stripped from the response for a caller without
 * `caregiver:read_pay` — §10.4 denies it to SCHEDULER deliberately — so the key
 * is missing rather than null. The column is therefore rendered conditionally
 * on the permission rather than on the value: a "—" in a Pay column tells a
 * scheduler there is a pay rate and they may not see it, which is one bit more
 * than they are entitled to and one bit more than the API gave them.
 *
 * Deciding it on the permission and not on `row.basePayRateCents === undefined`
 * also keeps the two answers from disagreeing when a caregiver genuinely has no
 * rate recorded.
 */

const STATUS_TONE: Record<string, StatusTone> = {
  APPLICANT: 'progress',
  ONBOARDING: 'progress',
  ACTIVE: 'verified',
  INACTIVE: 'inactive',
  TERMINATED: 'inactive',
  DO_NOT_RETURN: 'critical',
};

const STATUS_LABEL: Record<string, string> = {
  APPLICANT: 'Applicant',
  ONBOARDING: 'Onboarding',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  TERMINATED: 'Terminated',
  DO_NOT_RETURN: 'Do not return',
};

const DISCIPLINES = [
  'PCA',
  'HHA',
  'HOMEMAKER',
  'COMPANION',
  'RESPITE',
  'NURSING_SUPERVISORY',
  'OTHER',
] as const;

export interface CaregiverRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  employeeNumber: string | null;
  status: string;
  employmentType: string;
  disciplines: string[];
  languages: string[];
  maxWeeklyHours: number | null;
  /** Absent — not null — without `caregiver:read_pay`. */
  basePayRateCents?: number;
}

/** Cents to a currency string. Integer arithmetic only (rule 7). */
export function formatPayRate(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';

  const whole = Math.trunc(cents / 100);
  const part = String(cents % 100).padStart(2, '0');

  return `$${whole.toLocaleString('en-US')}.${part}`;
}

export function CaregiverList(): ReactNode {
  const api = useApi();
  const canSeePay = useHasPermission('caregiver:read_pay');
  const canCreate = useHasPermission('caregiver:create');

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [discipline, setDiscipline] = useState('');

  const query = useQuery({
    queryKey: ['caregivers', 'list', { search, status, discipline }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (status) params.set('status', status);
      if (discipline) params.set('discipline', discipline);

      return api.get<{ caregivers: CaregiverRow[]; total: number }>(
        `/v1/caregivers${params.size > 0 ? `?${params.toString()}` : ''}`,
      );
    },
    /*
     * Always refetch on mount.
     *
     * The default 30-second `staleTime` is right for most screens and wrong for
     * this one. The roster is what somebody returns to immediately after adding
     * a caregiver, activating one, or ending an employment — and being shown
     * the list from before their own write reads as the write having failed.
     * Somebody who adds a caregiver, comes back, and sees an empty roster adds
     * them again.
     *
     * `staleTime: 0` rather than relying on the invalidation alone, because the
     * invalidation only covers the paths this app knows about: a second tab, a
     * colleague's change, or the browser back button all arrive here without
     * one.
     */
    staleTime: 0,
  });

  const rows = query.data?.caregivers ?? [];

  const columns = useMemo<Column<CaregiverRow>[]>(() => {
    const base: Column<CaregiverRow>[] = [
      {
        key: 'name',
        header: 'Caregiver',
        render: (row) => (
          <Link
            to="/caregivers/$caregiverId"
            params={{ caregiverId: row.id }}
            className="text-brand font-medium hover:underline"
          >
            {row.firstName} {row.lastName}
          </Link>
        ),
      },
      {
        key: 'employeeNumber',
        header: 'Number',
        render: (row) => (
          <span className="font-mono text-sm" data-numeric="true">
            {row.employeeNumber ?? '—'}
          </span>
        ),
      },
      {
        key: 'disciplines',
        header: 'Disciplines',
        render: (row) => (
          <span className="flex flex-wrap gap-1">
            {row.disciplines.map((entry) => (
              <span
                key={entry}
                className="border-line text-ink-2 rounded-full border px-2 py-0.5 text-xs"
              >
                {entry.replace(/_/g, ' ').toLowerCase()}
              </span>
            ))}
          </span>
        ),
      },
      {
        key: 'maxWeeklyHours',
        header: 'Weekly cap',
        render: (row) => (
          <span className="text-ink-2 text-sm">
            {row.maxWeeklyHours === null ? '—' : `${row.maxWeeklyHours} h`}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => (
          <StatusPill tone={STATUS_TONE[row.status] ?? 'inactive'}>
            {STATUS_LABEL[row.status] ?? row.status}
          </StatusPill>
        ),
      },
    ];

    if (!canSeePay) return base;

    // Inserted before Status rather than appended, so the money sits with the
    // employment facts rather than after the lifecycle.
    return [
      ...base.slice(0, 4),
      {
        key: 'pay',
        header: 'Pay rate',
        render: (row) => (
          <span className="font-mono text-sm" data-numeric="true">
            {formatPayRate(row.basePayRateCents)}
          </span>
        ),
      },
      ...base.slice(4),
    ];
  }, [canSeePay]);

  return (
    <div className="grid gap-5">
      <PageHeader
        title="Caregivers"
        meta="Who is on the roster, what they are qualified for, and who can take a shift."
        action={
          canCreate ? (
            <Link to="/caregivers/new" className={buttonClasses({})}>
              Add a caregiver
            </Link>
          ) : undefined
        }
      />

      <Card>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field
            label="Search"
            hint="A name, an employee number or an email."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <SelectField
            label="Status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">Any status</option>
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Discipline"
            value={discipline}
            onChange={(event) => setDiscipline(event.target.value)}
          >
            <option value="">Any discipline</option>
            {DISCIPLINES.map((entry) => (
              <option key={entry} value={entry}>
                {entry.replace(/_/g, ' ').toLowerCase()}
              </option>
            ))}
          </SelectField>
        </div>
      </Card>

      <ErrorNote error={query.error} nextStep="Reload the page and try again." />

      {query.isPending ? (
        <Card>
          <p className="text-ink-3 py-10 text-center text-sm">Loading the roster…</p>
        </Card>
      ) : rows.length === 0 ? (
        <Card padded={false}>
          <EmptyState
            title={search || status || discipline ? 'Nobody matches that' : 'No caregivers yet'}
            explanation={
              search || status || discipline
                ? 'Try a different name, or clear the filters.'
                : 'A caregiver is somebody who can be assigned a visit. Add the first one to start building the roster.'
            }
            {...(canCreate && !search && !status && !discipline
              ? {
                  action: (
                    <Link to="/caregivers/new" className={buttonClasses({})}>
                      Add a caregiver
                    </Link>
                  ),
                }
              : {})}
          />
        </Card>
      ) : (
        <Card padded={false}>
          <DataTable
            caption={`${query.data?.total ?? rows.length} caregivers`}
            columns={columns}
            rows={rows}
            getRowKey={(row) => row.id}
            empty={{
              title: 'No caregivers yet',
              explanation: 'A caregiver is somebody who can be assigned a visit.',
            }}
          />
        </Card>
      )}
    </div>
  );
}
